import classnames from 'classnames/bind';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import React from 'react';
import throttle from 'lodash/throttle';

import AttachmentUploadButton from 'presentation/selfService/AttachmentUploadButton';
import { ERRORS } from 'enums/ui';
import InputBox from 'presentation/components/InputBox';
import ImagePreview from 'presentation/selfService/ImagePreview';
import { isIE, isMobileIos } from 'lib/getCustomerProperties';
import overrideStyle from 'lib/overrideStyle';
import SubmitButtonContainer from 'presentation/containers/SubmitButtonContainer';
import { TypingState } from 'enums/session';

import styles from 'presentation/components/styles/Composition.css';
let cx = classnames.bind(styles);

// Typing event is throttled to 1/5sec, stop typing event debounced every start typing call
// Stop typing timeout must be at least 500ms longer to allow time for next typing update
const START_TYPING_INTERVAL = 5000;
const STOP_TYPING_TIMEOUT = 5500;
const MAX_ATTACHMENT_SIZE = 25000000; // 25mb

class InputValidationComposition extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      shouldShowError: false,
    };

    this.addTouchScrolling = this.addTouchScrolling.bind(this);
    this.handleAttachmentChange = this.handleAttachmentChange.bind(this);
    this.handleCloseImage = this.handleCloseImage.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.isMobileIos = isMobileIos();
    this.removeTouchScrolling = this.removeTouchScrolling.bind(this);
    this.focusComposition = this.focusComposition.bind(this);
    this.preventScrollingFn = ev => ev.preventDefault();
  }

  componentDidMount() {
    if (this.props.shouldAutoFocus) {
      this.focusComposition();
    }
  }

  componentWillMount() {
    this.handleActiveTyping = throttle(this.handleTypingStart, START_TYPING_INTERVAL, { trailing: false });
    this.handleTypingTimeout = debounce(this.handleTypingStop, STOP_TYPING_TIMEOUT);
  }

  componentWillUnmount() {
    if (this.isMobileIos) {
      this.addTouchScrolling();
    }
  }

  focusComposition() {
    this.input.focus();
    // Must do the following for IE browsers, otherwise customer won't be able to type when the composition is initially focused
    if (isIE()) {
      setTimeout(() => {
        document.activeElement.blur();
        this.input.focus();
      }, 5);
    }
  }

  addTouchScrolling() {
    window.removeEventListener('touchmove', this.preventScrollingFn, { passive: false });
  }

  removeTouchScrolling() {
    window.addEventListener('touchmove', this.preventScrollingFn, { passive: false });
  }

  handleAttachmentChange(ev) {
    if (ev.target.files) {
      let files = ev.target.files;
      let newAttachments = [];

      // TODO: Only allowing one attachment per composition until we can find away around smooch's webhook issue
      //  we are running into issues where the webhook is being overloaded with requests and not all of the attachments
      //  are being given the `message:sent` event that lets us present the attachment at the right time

      // for (let i = 0; i < files.length; i++) {
      //   files[i] &&
      //     newAttachments.push({ blob: URL.createObjectURL(files[i]), fileType: files[i].type, file: files[i] });
      // }

      if (files[0] && files[0].size < MAX_ATTACHMENT_SIZE) {
        newAttachments.push({ blob: URL.createObjectURL(files[0]), fileType: files[0].type, file: files[0] });

        if (this.props.attachments) {
          newAttachments = this.props.attachments.concat(newAttachments);
        }
        this.props.onAttachmentChange(newAttachments);
      } else {
        this.props.onAttachmentError(ERRORS.FILE_SIZE);
      }
      this.input.focus();
    }
  }

  handleCloseImage(index) {
    let newAttachments = [...this.props.attachments];
    newAttachments.splice(index, 1);
    this.props.onAttachmentChange(newAttachments, this.props.compositionDraft);
  }

  handleInputChange(ev) {
    this.props.onChange(ev.target.value, this.props.attachments);
    this.handleActiveTyping();
  }

  handleKeyDown(ev) {
    let keyCode = ev.key;
    const shouldSend = keyCode === 13 || keyCode === 'Enter';

    if (shouldSend && !ev.shiftKey) {
      this.handleSubmit();
      ev.stopPropagation();
      ev.preventDefault();
      return false;
    }
  }

  handleSubmit() {
    this.handleTypingStop();
    if (this.props.isChatClientConnected && this.props.hasContent) {
      const currentInput = this.props.compositionDraft.trim();
      if (this.props.onValidate(currentInput)) {
        this.props.onSubmit(currentInput, this.props.attachments);
        this.setState({ shouldShowError: false });
      } else {
        this.setState({ shouldShowError: true });
      }
      this.input.focus();
    }
  }

  handleTypingStart() {
    this.props.onCustomerTypingStateUpdate && this.props.onCustomerTypingStateUpdate(TypingState.STARTED);
    this.handleTypingTimeout();
  }

  handleTypingStop() {
    this.props.onCustomerTypingStateUpdate && this.props.onCustomerTypingStateUpdate(TypingState.STOPPED);
  }

  renderAttachments() {
    return this.props.attachments.map((attachment, index) => (
      <ImagePreview
        id="imagePlaceholder"
        fileType={attachment.fileType}
        fileName={attachment.file.name}
        key={index}
        number={index}
        onCloseImage={this.handleCloseImage}
        src={attachment.blob}
      />
    ));
  }

  renderAttachmentButton() {
    if (this.props.canAddAttachments) {
      return (
        <AttachmentUploadButton
          hasContent={this.props.hasContent}
          role="button"
          onChange={this.handleAttachmentChange}
          onKeyDown={() => true}
          title="gladlyAttachmentInputButton"
        />
      );
    }
  }

  renderSubmitButton() {
    if (this.props.hasContent) {
      return (
        <SubmitButtonContainer
          aria-label="submitButton"
          canAddAttachments={this.props.canAddAttachments}
          hasContent={this.props.hasContent}
          role="button"
          onClick={this.handleSubmit}
          onKeyDown={this.handleSubmit}
          data-aid="gladlyChatCompositionSubmit"
        />
      );
    }
  }

  render() {
    // classname only applies when there there is content in the inputBox
    let hasAttachments = this.props.attachments && this.props.attachments[0];
    let compositionInputBoxClasses = cx(styles.composition_inputBox_withAttachments);
    let compositionClassnames = cx(styles.composition, { composition_selfService: this.props.isSelfService });
    return (
      <div className={compositionClassnames}>
        <InputFlexWrapper>
          {this.state.shouldShowError ? (
            <CompositionError id="gladlyCompositionError">{this.props.errorMessage}</CompositionError>
          ) : null}
          <InnerInputFlexWrapper hasAttachments={hasAttachments}>
            <pre className={styles.composition_spacer_withAttachments}>
              {this.props.compositionDraft}
              <br />
            </pre>
            <InputBox
              className={compositionInputBoxClasses}
              data-aid="gladlyChatCompositionInput"
              data-gramm_editor="false"
              onBlur={this.isMobileIos ? this.addTouchScrolling : null}
              onChange={this.handleInputChange}
              onFocus={this.isMobileIos ? this.removeTouchScrolling : null}
              onKeyDown={this.handleKeyDown}
              hasAttachments={hasAttachments}
              prompt={this.props.prompt}
              ref={input => (this.input = input)}
              role="form"
              value={this.props.compositionDraft}
            />
            {this.renderSubmitButton()}
            {this.renderAttachmentButton()}
          </InnerInputFlexWrapper>
          <PreviewWrapper hasAttachments={hasAttachments}>
            {hasAttachments ? this.renderAttachments() : null}
          </PreviewWrapper>
        </InputFlexWrapper>
      </div>
    );
  }
}

InputValidationComposition.propTypes = {
  canAddAttachments: PropTypes.bool,
  compositionDraft: PropTypes.string,
  errorMessage: PropTypes.string,
  isChatClientConnected: PropTypes.bool.isRequired,
  hasContent: PropTypes.bool.isRequired,
  onAttachmentChange: PropTypes.func,
  onAttachmentError: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onCustomerTypingStateUpdate: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  onValidate: PropTypes.func,
  prompt: PropTypes.string.isRequired,
};

const CompositionError = overrideStyle('span')`
  color: #D2220E;
  font-family: ${p => p.theme.fontLibrary.secondary};
  font-size: 11px;
  left: 20px;
  position: absolute;
  top: 10px;
  z-index: 1;
`;

const InputFlexWrapper = overrideStyle('span')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const InnerInputFlexWrapper = overrideStyle('span')`
  display: flex;
  flex-direction: row;
  max-height: ${p => (p.hasAttachments ? '100px' : 'none')};
`;

const PreviewWrapper = overrideStyle('span')`
  display: flex;
  flex-direction: row;
  overflow: auto;
  padding: ${p => (p.hasAttachments ? `4px 0px 16px 8px` : '0px')};
  width: 80%;
`;

export default InputValidationComposition;
